/**
 * Cryptographically random referrer code generator.
 *
 * Universal (browser + Node 18+ via globalThis.crypto.getRandomValues). The
 * alphabet deliberately excludes 0/O/1/l/I because referrer codes get said
 * out loud ("share this link: onecalla dot com slash question-r-equals...")
 * and ambiguous glyphs cause support pain. 54 symbols at 12 chars ≈ 69 bits
 * of entropy, which is well over the threshold for unguessability at our
 * scale.
 *
 * Uses rejection sampling (accept bytes below the highest multiple of the
 * alphabet length) so the distribution is unbiased. An oversized buffer
 * amortizes the getRandomValues call.
 */

const ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
const MAX_UNBIASED_BYTE = Math.floor(256 / ALPHABET.length) * ALPHABET.length;

export function generateReferrerCode(length = 12): string {
  if (length < 4 || length > 40) {
    throw new Error("Referrer code length must be between 4 and 40");
  }

  const out: string[] = [];
  const bufferSize = Math.max(32, Math.ceil(length * 1.3));
  let buffer = new Uint8Array(bufferSize);
  let idx = bufferSize; // force refill on first iteration

  while (out.length < length) {
    if (idx >= buffer.length) {
      globalThis.crypto.getRandomValues(buffer);
      idx = 0;
    }
    const byte = buffer[idx++];
    if (byte < MAX_UNBIASED_BYTE) {
      out.push(ALPHABET[byte % ALPHABET.length]);
    }
  }
  return out.join("");
}
